import { useState, type FormEvent } from 'react';
import { Sparkles, Check, ArrowRight, AlertTriangle } from 'lucide-react';
import { Spinner } from '@systemdox/ui';
import { specsApi } from '@/services/specsApi';
import {
  systemdoxRefineAdapter,
  validateRefinedDocument,
  hasBlockingIssues,
  type RefineAdapter,
  type RefinedDocument,
  type ValidationIssue,
} from '@/services/refineAdapter';
import { useNavigate } from 'react-router-dom';

/** The refine seam (#540): defaults to SystemDox AI, swappable for the
 *  Noteble capture adapter later without touching this component. */
export function AiSpecChat({ adapter = systemdoxRefineAdapter }: { adapter?: RefineAdapter }) {
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [generated, setGenerated] = useState<RefinedDocument | null>(null);
  const [issues, setIssues] = useState<ValidationIssue[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  async function handleGenerate(e: FormEvent) {
    e.preventDefault();
    if (!description.trim()) return;
    setLoading(true);
    setError('');
    setGenerated(null);
    setIssues([]);
    try {
      const result = await adapter.refine({ input: description, docType: 'requirement' });
      // R6: refined output is validated against the shared-specs structure
      // before it can be saved.
      setIssues(validateRefinedDocument(result, 'requirement'));
      setGenerated(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Generation failed');
    } finally {
      setLoading(false);
    }
  }

  async function handleSave() {
    if (!generated || hasBlockingIssues(issues)) return;
    setSaving(true);
    try {
      const { draftId } = await specsApi.createSpec(generated.name, '');
      await specsApi.updateSpec(draftId, {
        proposalContent: generated.proposal,
        taskContent: generated.tasks,
        requirements: generated.requirements.map((r) => ({
          reqId: '',
          name: r.name,
          content: r.content,
        })),
      });
      navigate(`/requirements/${draftId}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="flex items-center gap-2 mb-4">
        <Sparkles className="w-5 h-5 text-purple-500" />
        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
          Generate Spec with AI
        </h2>
      </div>

      <form onSubmit={handleGenerate} className="mb-6">
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Describe the feature you want to specify...&#10;&#10;Example: Add GitHub webhook handler that receives push events, verifies the signature, extracts changed markdown files, and queues them for re-indexing."
          className="w-full h-40 p-4 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-slate-900 dark:text-slate-100 resize-none mb-3"
        />
        {error && (
          <div className="p-3 rounded-md bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm mb-3">
            {error}
          </div>
        )}
        <button
          type="submit"
          disabled={loading || !description.trim()}
          className="flex items-center gap-2 px-4 py-2 rounded-md bg-purple-600 text-white text-sm font-medium hover:bg-purple-700 disabled:opacity-50"
        >
          {loading ? (
            <>
              <Spinner size="sm" tone="current" />
              Generating...
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4" />
              Generate Spec
            </>
          )}
        </button>
      </form>

      {generated && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
              <Check className="w-4 h-4" /> Generated: {generated.name}
            </h3>
            <button
              onClick={handleSave}
              disabled={saving || hasBlockingIssues(issues)}
              title={hasBlockingIssues(issues) ? 'Resolve the validation errors before saving' : undefined}
              className="flex items-center gap-1 px-3 py-1.5 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save as Requirement'}
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Shared-specs validation (R6): errors block the save, warnings
              flag convention gaps to fix in the editor. */}
          {issues.length > 0 && (
            <ul className="space-y-1">
              {issues.map((issue, i) => (
                <li
                  key={i}
                  className={`flex items-start gap-1.5 text-xs ${
                    issue.severity === 'error'
                      ? 'text-red-600 dark:text-red-400'
                      : 'text-amber-600 dark:text-amber-400'
                  }`}
                >
                  <AlertTriangle className="w-3.5 h-3.5 mt-px shrink-0" />
                  {issue.message}
                </li>
              ))}
            </ul>
          )}

          <details className="border border-slate-200 dark:border-slate-700 rounded-lg">
            <summary className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 cursor-pointer">
              Proposal
            </summary>
            <pre className="px-4 py-3 text-xs text-slate-600 dark:text-slate-400 whitespace-pre-wrap border-t border-slate-200 dark:border-slate-700">
              {generated.proposal}
            </pre>
          </details>

          {generated.requirements.map((req, i) => (
            <details key={i} className="border border-slate-200 dark:border-slate-700 rounded-lg">
              <summary className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 cursor-pointer">
                Requirement: {req.name}
              </summary>
              <pre className="px-4 py-3 text-xs text-slate-600 dark:text-slate-400 whitespace-pre-wrap border-t border-slate-200 dark:border-slate-700">
                {req.content}
              </pre>
            </details>
          ))}

          <details className="border border-slate-200 dark:border-slate-700 rounded-lg">
            <summary className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 cursor-pointer">
              Tasks
            </summary>
            <pre className="px-4 py-3 text-xs text-slate-600 dark:text-slate-400 whitespace-pre-wrap border-t border-slate-200 dark:border-slate-700">
              {generated.tasks}
            </pre>
          </details>
        </div>
      )}
    </div>
  );
}
